import { useMemo, useState } from 'react'

import { useEditor } from '@craftjs/core'
import { omit } from 'lodash'
import { Info, Trash } from 'lucide-react'
import { Badge, Button, ButtonIcon, Checkbox, cn, DefaultPopover, DefaultTable, DefaultTooltip, HeaderSearch, Separator, Tabs, TabsList, TabsTrigger } from 'twyne-new.ui'

import { CodeEditor } from '@/components/CodeEditor'
import { TruncateWithTooltipFormatter } from '@/components/Grid/components/Table/formatters'
import { DEFAULT_FORM_UTILITIES } from '@/components/builder/blocks/Form/Container'
import { SHARED_PATTERN_DEFS } from '@/components/builder/utils'
import { useActiveSharedPattern, useFontConfig, useKeyframeConfig, useStylePatterns, useTailwindConfig } from '@/components/builder/utils/tailwindConfig/hooks'

function useNodesUsage() {
  const { nodes } = useEditor(state => ({ nodes: state.nodes }))

  return useMemo(() => {
    const list = Object.values(nodes)
    const names = list.map(({ data }) => data?.name)

    const isUsed = (prop, key) => list.some(({ data }) => JSON.stringify(data?.props?.properties?.[prop] ?? '').includes(`"${key}"`))

    return { names, isUsed }
  }, [nodes])
}

function isDeletable({ used, locked }) {
  return !used && !locked
}

function isChecked(value) {
  return typeof value === 'boolean' ? value : !!value?.target?.checked
}

function useConfigTypes() {
  const fonts = useFontConfig()
  const keyframes = useKeyframeConfig()
  const patterns = useStylePatterns()
  const utilities = useTailwindConfig('utilities')
  const isActivePattern = useActiveSharedPattern()
  const { names, isUsed } = useNodesUsage()

  return useMemo(() => [
    {
      value: 'fonts',
      label: 'Fonts',
      info: 'Fonts selected in the builder. Each one is imported into the page CSS, even when no element uses it anymore.',
      items: Object.entries(fonts.value || {}).map(([key, url]) => ({
        key,
        title: key.replace(/_/g, ' '),
        description: url,
        used: isUsed('fontFamily', key),
      })),
      remove: keys => fonts.setValue(omit(fonts.value, keys)),
    },
    {
      value: 'keyframes',
      label: 'Applied keyframes',
      info: 'Animations applied to elements. Each one adds a @keyframes rule to the page CSS.',
      items: (keyframes.value || []).map(key => ({
        key,
        title: key,
        used: isUsed('animation', key),
      })),
      remove: keys => keyframes.setValue(keyframes.value.filter(name => !keys.includes(name))),
    },
    {
      value: 'stylePatterns',
      label: 'Style patterns',
      info: 'Reusable style presets shared between elements. Form patterns are created automatically by the Form block.',
      items: Object.keys(patterns.value || {}).map(key => ({
        key,
        title: key,
        details: patterns.value[key],
        used: isUsed('stylePattern', key),
        locked: SHARED_PATTERN_DEFS[key] && isActivePattern(key) && `Can't delete while a ${key.startsWith('tcpa-') ? 'Midpath' : 'Form'} is on the page. It's recreated automatically.`,
      })),
      remove: keys => patterns.setValue(omit(patterns.value, keys)),
    },
    {
      value: 'utilities',
      label: 'Tailwind utilities',
      info: 'Global CSS classes added to the page. The Form block adds its grid classes here.',
      items: Object.entries(utilities.value || {}).map(([key, className]) => ({
        key,
        title: key,
        description: className,
        locked: DEFAULT_FORM_UTILITIES[key] && names.includes('form') && 'Managed by the Form block',
      })),
      remove: keys => utilities.setValue(omit(utilities.value, keys)),
    },
  ], [fonts, keyframes, patterns, utilities, isActivePattern, names, isUsed])
}

export default function AdvancedSettings() {
  const types = useConfigTypes()
  const [selected, setSelected] = useState(types[0].value)
  const [search, setSearch] = useState('')
  const [focused, setFocused] = useState(false)
  const [checked, setChecked] = useState([])

  const type = types.find(({ value }) => value === selected)
  const items = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return type.items

    return type.items.filter(({ title, description }) => [title, description].some(text => text?.toLowerCase().includes(term)))
  }, [type, search])

  const deletableKeys = items.filter(isDeletable).map(({ key }) => key)
  const selectedKeys = checked.filter(key => deletableKeys.includes(key))
  const allChecked = !!deletableKeys.length && deletableKeys.every(key => selectedKeys.includes(key))

  const handleCheckAll = value => setChecked(isChecked(value) ? deletableKeys : [])
  const handleCheck = (key, value) => setChecked(current => isChecked(value) ? [...current, key] : current.filter(item => item !== key))

  const handleRemoveSelected = () => {
    type.remove(selectedKeys)
    setChecked([])
  }

  const columns = [
    {
      dataField: 'key',
      text: () => <Checkbox value={allChecked} onChange={handleCheckAll} disabled={!deletableKeys.length} />,
      headerClassName: 'w-4',
      className: 'w-4',
      formatter: (key, row) => <Checkbox value={selectedKeys.includes(key)} onChange={value => handleCheck(key, value)} disabled={!isDeletable(row)} />,
    },
    { dataField: 'title', text: 'Name', className: 'w-120' },
    {
      dataField: 'description',
      text: 'Value',
      className: 'w-100',
      formatter: (description, { details }) => details ? <DetailsPopover details={details} /> : <TruncateWithTooltipFormatter value={description} className='max-w-100 twy-text-base-neutral' />,
    },
    {
      dataField: 'used',
      text: type.items.some(({ used }) => used !== undefined) ? 'Status' : '',
      headerClassName: 'w-50',
      formatter: used => used !== undefined && <Badge variant='outline' color={used ? 'accent' : 'neutral'} size='sm'>{used ? 'In use' : 'Unused'}</Badge>,
    },
    {
      dataField: 'key',
      text: '',
      className: 'w-10 flex justify-center',
      formatter: (key, { used, locked }) => <RemoveButton used={used} locked={locked} onRemove={() => type.remove([key])} />,
    },
  ]

  return <div className='flex flex-col gap-4'>
    <div className='flex items-center justify-between gap-4 border-b twy-border-base'>
      <Tabs value={selected} onValueChange={setSelected} size='sm'>
        <TabsList className='border-b-0'>
          {types.map(({ value, label, info, items }) => <TabsTrigger key={value} value={value} className='flex items-center gap-2 pt-0!'>
            {label}
            <TypeInfo info={info} />
            <Badge variant='light' color='neutral' size='sm'>{items.length}</Badge>
          </TabsTrigger>)}
        </TabsList>
      </Tabs>
      <HeaderSearch focused={focused || !!search} onFocus={setFocused} value={search} onChange={setSearch} className='-mt-2' />
    </div>

    <div className='h-[48vh] overflow-y-auto border twy-border-base rounded-xs'>
      <BulkActions count={selectedKeys.length} onRemove={handleRemoveSelected} />
      <DefaultTable
        columns={columns}
        data={items}
        keyField='key'
        noDataIndication={() => 'No items'}
      />
    </div>
  </div>
}

function BulkActions({ count, onRemove }) {
  return <div className={cn(
    'w-full flex items-center px-5 gap-2 overflow-hidden transition-all duration-200 ease-in-out',
    count ? 'max-h-14 py-3 opacity-100 translate-y-0 border-b twy-border-base' : 'max-h-0 py-0 opacity-0 -translate-y-1 border-b border-transparent pointer-events-none',
  )}>
    <div>
      <span className='twy-text-base-accent mr-1'>{count}</span>
      <span>{count === 1 ? 'item' : 'items'} selected.</span>
    </div>
    <Separator orientation='vertical' className='py-2' />
    <Button variant='outline' color='danger' size='sm' onClick={onRemove}>Delete selected</Button>
  </div>
}

function TypeInfo({ info }) {
  return <DefaultTooltip content={info} contentProps={{ className: 'max-w-60' }} triggerProps={{ asChild: true }}>
    <Info size={14} className='twy-text-base-neutral' />
  </DefaultTooltip>
}

function DetailsPopover({ details }) {
  return <DefaultPopover
    trigger={<ButtonIcon content='View value'>
      <Info size={16} />
    </ButtonIcon>}
    contentProps={{ className: 'w-[500px]', side: 'right' }}
    title='Value'
  >
    <CodeEditor value={JSON.stringify(details, null, 2)} width='100%' height='300px' language='json' readOnly />
  </DefaultPopover>
}

function RemoveButton({ used, locked, onRemove }) {
  const reason = locked || (used && "Can't delete because it's in use. Remove it from the elements first")

  return <ButtonIcon content={reason || 'Delete'} disabled={!!reason} onClick={onRemove} className='flex justify-end'>
    <Trash size={16} />
  </ButtonIcon>
}
